/**
 * La barre d'actions flottante, au-dessus de la sélection.
 *
 * Le serveur rend un gabarit par outil que l'éditeur propose (Fabric::ActionBarComponent) ;
 * la lib dit quels outils montrer et dans quel ordre (`toolsFor`). La barre clone les
 * gabarits dans cet ordre — huit boutons au plus : au-delà, les premiers outils, « Plus »
 * (le menu des autres), puis Supprimer ; deux outils liés (Avancer et Reculer) ne se
 * séparent pas. Elle ne reconstruit que si la liste change : un menu ouvert survit à une
 * modification.
 *
 * Elle règle ce qui suit l'objet (l'icône du verrou, le mode du groupe, la pastille de
 * couleur) et l'outil ouvert — celui dont la sous-page est dans la toolbox : une pastille
 * glisse jusqu'à lui (jusqu'à « Plus » s'il y est rangé).
 *
 * Elle ne touche à aucune classe : elle pose des attributs (`hidden`, `data-state`,
 * `data-placement`, `data-open`, `data-on`, `aria-pressed`) que les classes du composant
 * lisent, et les positions calculées en style.
 */
import type { FabricObject } from "fabric";

/** Ce qui, sur l'objet (ou la page), se lit dans la barre. */
export interface ActionBarState {
  lockMode?: string
  arrangement?: string
  /** Le sens d'une pile (`row` | `column`) : l'icône de Rangé le suit. */
  direction?: string
  color?: string | null
  /** Le fond de la page sélectionnée (`image`, `video`…) : son outil devient « Remplacer… ». */
  backgroundKind?: string
  /** La condition de visibilité du calque, pour le sujet d'aperçu : l'œil de l'outil
   *  Condition se ferme sur un calque masqué. */
  condition?: "shown" | "hidden"
  /** La vitesse de la séquence sélectionnée (1 : normale) : la légende de l'outil la dit,
   *  son menu la marque. */
  speed?: number
  /** La séquence sélectionnée se répète (stade template) : l'outil est enfoncé. */
  repeat?: boolean
}

/** Où dessiner la barre : la géométrie de l'éditeur (FabricEditor#getControlsConfig). */
export interface ActionBarGeometry {
  getContainer: () => HTMLElement
  getDisplayScale: () => number
  getCanvasOffset: () => { left: number; top: number }
  /** Un placement à soi (tests, éditeurs particuliers) à la place du calcul par défaut. */
  position?: (el: HTMLElement, obj: FabricObject) => void
  /** Le raccourci qui double un outil (« ⌫ ou Suppr »), pour son infobulle. */
  shortcutFor?: (tool: string) => string | null
}

type Rect = { left: number; top: number; width: number; height: number }

/** Huit boutons visibles au plus, Supprimer compris. */
export const MAX_BUTTONS = 8;
/** La barre apparaît un instant après la sélection : un clic qui traverse ne la fait pas clignoter. */
const APPEAR_DELAY = 100;
/** Sous ce haut de sélection (px du conteneur), la barre passe en dessous. */
const FLIP_BELOW = 56;
const GAP_ABOVE = 10;
const GAP_BELOW = 14;
/** La marge que la barre garde avec les bords du conteneur. */
const EDGE = 8;
/** La barre de la page se pose dans le cadre, en haut à gauche, à cette distance de ses bords. */
const FRAME_INSET = 12;
const CHECKER = "repeating-conic-gradient(#d4d4d8 0 25%, #fff 0 50%) 0 0 / 6px 6px";

/**
 * La part de la barre et celle du menu « Plus ». Supprimer n'y est pas : il reste en
 * dernier, dans la barre. `slots` : les boutons qu'un outil occupe ; `pair` : son lien ;
 * `extra` : un outil d'appoint, toujours dans « Plus ».
 */
export function splitTools<T extends string>(
  tools: T[],
  { slots, pair, extra = () => false, max = MAX_BUTTONS }: {
    slots: (tool: T) => number; pair: (tool: T) => string | undefined; extra?: (tool: T) => boolean; max?: number
  },
): { bar: T[]; more: T[] } {
  const deletes = tools.includes("delete" as T);
  const main = tools.filter((tool) => tool !== "delete" && !extra(tool));
  const extras = tools.filter((tool) => extra(tool));
  const count = (list: T[]) => list.reduce((sum, tool) => sum + slots(tool), 0);
  if (!extras.length && count(main) + (deletes ? 1 : 0) <= max) return { bar: main, more: [] };

  // « Plus » prend une place, Supprimer la sienne
  const budget = max - 1 - (deletes ? 1 : 0);
  const bar: T[] = [];
  const more: T[] = [];
  for (const tool of main) {
    if (bar.includes(tool) || more.includes(tool)) continue;
    const group = pair(tool) ? main.filter((other) => pair(other) === pair(tool)) : [tool];
    if (more.length === 0 && count(bar) + count(group) <= budget) bar.push(...group);
    else more.push(...group);
  }
  return { bar, more: [...more, ...extras] };
}

export class FabricActionBar {
  readonly el: HTMLElement
  /** Les outils que l'éditeur propose : ceux dont le serveur a rendu le gabarit. */
  readonly offered: string[]
  private readonly row: HTMLElement
  private readonly slot: HTMLElement
  private readonly pill: HTMLElement | null
  private readonly menu: HTMLElement | null
  private readonly geometry: ActionBarGeometry
  private shownTools = ""
  private openPage: string | null = null
  private appearTimer: ReturnType<typeof setTimeout> | null = null
  private readonly onDocumentPointerDown = (e: Event) => {
    if (!this.el.contains(e.target as Node)) this.closeMenu();
  };
  private readonly onDocumentKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.closeMenu();
  };

  constructor(el: HTMLElement, geometry: ActionBarGeometry) {
    this.el = el;
    this.geometry = geometry;
    this.offered = JSON.parse(el.dataset.tools || "[]");
    this.row = el.querySelector<HTMLElement>("[data-action-bar-row]") || el;
    this.slot = el.querySelector<HTMLElement>("[data-action-bar-slot]") || el;
    this.pill = el.querySelector<HTMLElement>("[data-action-bar-pill]");
    this.menu = el.querySelector<HTMLElement>("[data-action-bar-menu]");

    el.addEventListener("click", (e) => this.onClick(e));
    // Un bouton de la barre ne prend pas le focus : un texte en édition sur le canvas garde
    // le sien et sa sélection — l'outil s'applique à la plage, la frappe continue
    el.addEventListener("mousedown", (e) => {
      if ((e.target as HTMLElement).closest("button")) e.preventDefault();
    });
    document.addEventListener("pointerdown", this.onDocumentPointerDown);
    document.addEventListener("keydown", this.onDocumentKeyDown);
  }

  destroy(): void {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    document.removeEventListener("keydown", this.onDocumentKeyDown);
    if (this.appearTimer) clearTimeout(this.appearTimer);
  }

  get visible(): boolean {
    return !this.el.hidden;
  }

  /** Montre ces outils, dans cet ordre, réglés sur l'état de l'objet. */
  show(tools: string[], state: ActionBarState = {}): void {
    this.build(tools);
    this.sync(state);
    if (tools.length === 0) {
      this.hide();
      return;
    }
    if (!this.el.hidden) return;

    // Montée invisible (on la mesure et la place), puis apparition
    this.el.hidden = false;
    this.el.dataset.state = "closed";
    this.appearTimer = setTimeout(() => {
      this.el.dataset.state = "open";
      this.appearTimer = null;
    }, APPEAR_DELAY);
  }

  hide(): void {
    if (this.appearTimer) clearTimeout(this.appearTimer);
    this.appearTimer = null;
    this.el.hidden = true;
    this.el.dataset.state = "closed";
    this.closeMenu();
  }

  /** L'outil ouvert : celui dont la sous-page est dans la toolbox (null : aucune). */
  markOpen(page: string | null): void {
    this.openPage = page;
    this.applyOpen();
  }

  /**
   * Centre la barre au-dessus de l'objet — en dessous s'il touche le haut —, dans le
   * conteneur, à distance de ses bords. Trop étroit : elle défile.
   */
  place(obj: FabricObject): void {
    if (this.geometry.position) {
      this.geometry.position(this.el, obj);
      return;
    }
    const container = this.geometry.getContainer();
    if (!container) return;

    this.el.style.maxWidth = `${Math.max(0, container.clientWidth - 2 * EDGE)}px`;
    const rect = this.displayRect(obj);
    const width = this.el.offsetWidth || 200;
    const height = this.el.offsetHeight || 58;
    const below = rect.top < FLIP_BELOW;

    const left = clamp(rect.left + rect.width / 2 - width / 2, EDGE, container.clientWidth - width - EDGE);
    const top = clamp(
      below ? rect.top + rect.height + GAP_BELOW : rect.top - height - GAP_ABOVE,
      EDGE, container.clientHeight - height - EDGE,
    );

    this.el.dataset.placement = below ? "below" : "above";
    Object.assign(this.el.style, { left: `${left}px`, top: `${top}px` });
    this.applyOpen();
  }

  /**
   * La barre de la page : ancrée au coin haut gauche du cadre — sous le nom de la page —,
   * mais large comme elle veut, dans le conteneur, comme celle des calques. Un cadre étroit
   * (format mobile) ne l'écrase pas : elle déborde du cadre plutôt que de perdre des boutons.
   */
  placeInFrame(frame: Rect): void {
    const container = this.geometry.getContainer();
    if (!container) return;

    this.el.style.maxWidth = `${Math.max(0, container.clientWidth - 2 * EDGE)}px`;
    const width = this.el.offsetWidth || 200;
    const height = this.el.offsetHeight || 58;

    this.el.dataset.placement = "below";
    Object.assign(this.el.style, {
      left: `${clamp(frame.left + FRAME_INSET, EDGE, container.clientWidth - width - EDGE)}px`,
      top: `${clamp(frame.top + FRAME_INSET, EDGE, container.clientHeight - height - EDGE)}px`,
    });
    this.applyOpen();
  }

  /** Au bas du conteneur, centrée : la barre d'une séquence, contre la timeline sous elle. */
  placeAtBottom(): void {
    const container = this.geometry.getContainer();
    if (!container) return;

    this.el.style.maxWidth = `${Math.max(0, container.clientWidth - 2 * EDGE)}px`;
    const width = this.el.offsetWidth || 200;
    const height = this.el.offsetHeight || 58;

    this.el.dataset.placement = "above";
    Object.assign(this.el.style, {
      left: `${Math.max(EDGE, (container.clientWidth - width) / 2)}px`,
      top: `${Math.max(EDGE, container.clientHeight - height - EDGE)}px`,
    });
    this.applyOpen();
  }

  /** La boîte de l'objet, en pixels du conteneur. */
  displayRect(obj: FabricObject): Rect {
    const scale = this.geometry.getDisplayScale();
    const offset = this.geometry.getCanvasOffset();
    const bound = obj.getBoundingRect();
    return {
      left: bound.left * scale + offset.left,
      top: bound.top * scale + offset.top,
      width: bound.width * scale,
      height: bound.height * scale,
    };
  }

  // ==================== Construction ====================

  private build(tools: string[]): void {
    const key = tools.join(" ");
    if (key === this.shownTools) return;
    this.shownTools = key;

    const { bar, more } = splitTools(tools, {
      slots: (tool) => (this.template(tool) ? Number(this.template(tool)!.dataset.slots || 1) : 0),
      pair: (tool) => this.template(tool)?.dataset.pair,
      extra: (tool) => this.template(tool)?.dataset.more !== undefined,
    });
    const tail: string[] = more.length ? ["more"] : [];
    if (tools.includes("delete")) tail.push("delete");

    this.slot.replaceChildren(...[...bar, ...tail].flatMap((tool) => this.clone(tool, tools)));
    this.menu?.replaceChildren(...more.flatMap((tool) => this.clone(tool, tools)));
    this.closeMenu();
  }

  private template(tool: string): HTMLTemplateElement | null {
    return this.el.querySelector<HTMLTemplateElement>(`:scope > template[data-tool="${tool}"]`);
  }

  private clone(tool: string, tools: string[]): DocumentFragment[] {
    const template = this.template(tool);
    if (!template) return [];
    const fragment = template.content.cloneNode(true) as DocumentFragment;
    pruneTools(fragment, tools);
    // Le bouton dit quel outil il est : une bulle s'y ancre (`[data-tool-id='group']`)
    for (const el of Array.from(fragment.children) as HTMLElement[]) el.dataset.toolId = tool;
    this.labelShortcut(fragment, tool);
    return [fragment];
  }

  /** L'outil qu'une touche double le dit dans son infobulle : « Supprimer · ⌫ ou Suppr ». */
  private labelShortcut(fragment: DocumentFragment, tool: string): void {
    const shortcut = this.geometry.shortcutFor?.(tool);
    const button = fragment.querySelector<HTMLElement>("button, a");
    if (!shortcut || !button || button.dataset.controller) return;
    button.dataset.controller = "tooltip";
    button.dataset.tooltipTextValue = `${button.getAttribute("aria-label") || ""} · ${shortcut}`.replace(/^ · /, "");
  }

  private sync({ lockMode, arrangement, direction, color, backgroundKind, condition, speed, repeat }: ActionBarState): void {
    this.el.querySelectorAll<HTMLElement>("[data-lock-mode]").forEach((node) => {
      node.hidden = node.dataset.lockMode !== (lockMode || "free");
    });
    // Le mode du verrou marqué dans son menu — un gabarit, cloné à l'ouverture : c'est lui
    // qu'on règle
    this.menuTemplates().forEach((content) => {
      content.querySelectorAll<HTMLElement>("[data-lock-option]").forEach((option) => {
        option.setAttribute("aria-pressed", String(option.dataset.lockOption === (lockMode || "free")));
      });
      content.querySelectorAll<HTMLElement>("[data-speed-option]").forEach((option) => {
        option.setAttribute("aria-pressed", String(Number(option.dataset.speedOption) === (speed || 1)));
      });
    });
    // La vitesse posée en légende de son outil, son nom à vitesse normale
    this.el.querySelectorAll<HTMLElement>("[data-speed-caption]").forEach((caption) => {
      caption.textContent = speed && speed !== 1 ? `${speed}x` : caption.dataset.speedCaption || "";
    });
    this.el.querySelectorAll<HTMLElement>("[data-sequence-repeat]").forEach((button) => {
      button.setAttribute("aria-pressed", String(Boolean(repeat)));
    });
    this.el.querySelectorAll<HTMLElement>("[data-background-label]").forEach((label) => {
      label.hidden = label.dataset.backgroundLabel !== backgroundKind;
    });
    this.el.querySelectorAll<HTMLElement>("[data-background-unless]").forEach((label) => {
      label.hidden = label.dataset.backgroundUnless === backgroundKind;
    });
    // L'icône de l'outil Condition : l'œil fermé sur un calque masqué, ouvert sinon
    this.el.querySelectorAll<HTMLElement>("[data-condition-state]").forEach((icon) => {
      icon.hidden = (icon.dataset.conditionState === "hidden") !== (condition === "hidden");
    });
    this.el.querySelectorAll<HTMLElement>("[data-arrangement]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.arrangement === (arrangement ?? "stack")));
    });
    this.el.querySelectorAll<HTMLElement>("[data-direction]").forEach((icon) => {
      icon.hidden = icon.dataset.direction !== (direction === "row" ? "row" : "column");
    });
    if (color) {
      this.el.querySelectorAll<HTMLElement>("[data-role='currentColor']").forEach((swatch) => {
        swatch.style.background = color === "transparent" ? CHECKER : color;
      });
    }
    this.applyOpen();
  }

  /** Les gabarits des menus des outils montrés (Aligner, Verrou) : leur contenu. */
  private menuTemplates(): DocumentFragment[] {
    return [this.slot, this.menu].flatMap((root) =>
      root ? Array.from(root.querySelectorAll("template")).map((template) => template.content) : []);
  }

  // ==================== L'outil ouvert ====================

  private applyOpen(): void {
    this.el.querySelectorAll<HTMLElement>("[data-open]").forEach((node) => delete node.dataset.open);

    const tool = this.openPage ? this.el.querySelector<HTMLElement>(`[data-page="${this.openPage}"]`) : null;
    const inMenu = Boolean(tool && this.menu?.contains(tool));
    const moreButton = this.slot.querySelector<HTMLElement>("[data-action-bar-more]");
    if (tool) tool.dataset.open = "";
    if (inMenu && moreButton) moreButton.dataset.open = "";
    this.movePill(inMenu ? moreButton : tool);
  }

  private movePill(anchor: HTMLElement | null): void {
    if (!this.pill) return;
    if (!anchor || !this.row.contains(anchor)) {
      delete this.pill.dataset.on;
      return;
    }
    this.pill.style.width = `${anchor.offsetWidth}px`;
    this.pill.style.transform = `translateX(${anchor.offsetLeft}px)`;
    this.pill.dataset.on = "";
  }

  // ==================== « Plus » ====================

  private onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (target.closest("[data-action-bar-more]")) {
      this.toggleMenu();
      return;
    }
    // Un outil du menu a servi : le menu se ferme, après lui
    if (this.menu?.contains(target) && target.closest("button, a")) setTimeout(() => this.closeMenu(), 0);
  }

  private toggleMenu(): void {
    if (!this.menu) return;
    this.menu.hidden = !this.menu.hidden;
    this.slot.querySelector("[data-action-bar-more]")?.setAttribute("aria-expanded", String(!this.menu.hidden));
  }

  private closeMenu(): void {
    if (!this.menu || this.menu.hidden) return;
    this.menu.hidden = true;
    this.slot.querySelector("[data-action-bar-more]")?.setAttribute("aria-expanded", "false");
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, Math.max(min, max)));
}

/** Retire d'un gabarit cloné les outils absents de la liste, menus (gabarits imbriqués) compris. */
function pruneTools(root: DocumentFragment | Element, tools: string[]): void {
  root.querySelectorAll<HTMLElement>("[data-tool]").forEach((el) => {
    if (!tools.includes(el.dataset.tool!)) el.remove();
  });
  root.querySelectorAll("template").forEach((template) => pruneTools(template.content, tools));
}
